import React from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import type { MonitoringAiSettings } from '../../constants/monitoringAiSettings';

export type YoloModelFilesResponse = {
  files: string[];
  status?: {
    configured_model_path?: string;
    model_file_exists?: boolean;
    yolo_pipeline_preset?: string;
    active_singleton_model_path?: string;
  };
};

const PRESET_OPTIONS: {
  value: MonitoringAiSettings['yolo_pipeline_preset'];
  label: string;
  detail: string;
}[] = [
  {
    value: 'dedicated',
    label: '전용 PT 단독 (권장)',
    detail: '학습한 weights 하나만 사용. COCO·하이브리드 보조 없음.',
  },
  {
    value: 'hybrid_recall',
    label: '전용 + COCO 리콜 보조',
    detail: '미검이 많을 때 ver5+COCO 하이브리드(구 bat 프로필 — _archive/restart_profiles_*).',
  },
  {
    value: 'coco_only_debug',
    label: 'COCO만 (디버그)',
    detail: 'yolov8s person 위주 — 사선은 타일(ROI) 추론 ON. 전용 모델 검증·비교용.',
  },
  {
    value: 'raw_person_debug',
    label: '원본 YOLO (진단)',
    detail:
      '필터 없이 person + 타일(ROI) 추론. 모델 미탐 vs 필터 구분용. 운영용 아님.',
  },
];

type Props = {
  value: MonitoringAiSettings;
  onChange: (next: MonitoringAiSettings) => void;
  onSave: () => void;
  saving?: boolean;
  modelFiles: string[];
  yoloStatus?: YoloModelFilesResponse['status'];
};

const YoloDetectorSettingsCard: React.FC<Props> = ({
  value,
  onChange,
  onSave,
  saving = false,
  modelFiles,
  yoloStatus,
}) => {
  const fileList = modelFiles.length > 0 ? modelFiles : [value.default_yolo_model];
  const uniqueFiles = Array.from(new Set(fileList.filter(Boolean)));

  return (
    <Card sx={{ mb: 0 }}>
      <CardHeader
        title="YOLO 검출 모델 (전역)"
        subheader="시설별 ai_detector_model 이 있으면 그 시설만 예외. 저장 후 가중치·프리셋 변경은 백엔드 재시작을 권장."
        action={
          <Button
            size="small"
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={onSave}
            disabled={saving}
          >
            저장
          </Button>
        }
      />
      <CardContent>
        <FormControl fullWidth margin="dense" sx={{ mb: 2, maxWidth: 520 }}>
          <InputLabel id="default-yolo-model-label">기본 weights (models/)</InputLabel>
          <Select
            labelId="default-yolo-model-label"
            label="기본 weights (models/)"
            value={
              uniqueFiles.includes(value.default_yolo_model)
                ? value.default_yolo_model
                : value.default_yolo_model
            }
            onChange={(e) =>
              onChange({ ...value, default_yolo_model: String(e.target.value) })
            }
          >
            {!uniqueFiles.includes(value.default_yolo_model) && value.default_yolo_model ? (
              <MenuItem value={value.default_yolo_model}>
                {value.default_yolo_model} (목록에 없음)
              </MenuItem>
            ) : null}
            {uniqueFiles.map((f) => (
              <MenuItem key={f} value={f}>
                {f}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
          학습한.pt 는 <code>backend/models/</code> 에 복사한 뒤 목록에서 선택.
          부하 완화용 Nano는 파일이 있을 때 <code>yolov8n.pt</code> 선택(「Nano
          선택」). 저장·재시작 후 검출률 확인. 기본 모델은 자동 변경 없음.
        </Typography>

        <FormControl fullWidth margin="dense" sx={{ mb: 1, maxWidth: 520 }}>
          <InputLabel id="yolo-pipeline-preset-label">추론 파이프라인</InputLabel>
          <Select
            labelId="yolo-pipeline-preset-label"
            label="추론 파이프라인"
            value={value.yolo_pipeline_preset}
            onChange={(e) =>
              onChange({
                ...value,
                yolo_pipeline_preset: e.target.value as MonitoringAiSettings['yolo_pipeline_preset'],
              })
            }
          >
            {PRESET_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {PRESET_OPTIONS.find((o) => o.value === value.yolo_pipeline_preset)?.detail}
        </Typography>

        {yoloStatus?.configured_model_path ? (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            적용 경로(설정): {yoloStatus.configured_model_path}
            {yoloStatus.model_file_exists === false ? ' — 파일 없음' : ''}
          </Typography>
        ) : null}

        <Alert severity="info" sx={{ mt: 1 }}>
          저장하면 파이프라인 플래그는 즉시 반영됨. <strong>weights 파일을 바꾼 경우</strong>에는
          <strong>백엔드 재시작</strong>(예: backend/restart.bat) 후 관제에서 확인.
        </Alert>
        {value.yolo_pipeline_preset === 'raw_person_debug' ? (
          <Alert severity="warning" sx={{ mt: 1 }}>
            <strong>원본 YOLO(진단)</strong>은 오탐·위험 필터를 끔. 성북 등에서 「사람이 안
            잡히는지 / 필터가 지우는 건지」만 볼 때 사용. 확인 후 바로 다른 프리셋으로 되돌림.
          </Alert>
        ) : null}
      </CardContent>
    </Card>
  );
};

export default YoloDetectorSettingsCard;
